import React, { useMemo } from 'react';
import {
  DoubleSide,
  PlaneGeometry,
  TextureLoader,
  RepeatWrapping,
  BackSide,
  LinearFilter,
  NoColorSpace,
} from 'three';
import { Water } from './Water2.js';
import pzPng from './bg/river/pz.png';
import nxPng from './bg/river/nx.png';
import pxPng from './bg/river/px.png';
import nyPng from './bg/river/ny.png';
import normal0Jpg from './textures/Water_1_M_Normal.jpg';
import normal1Jpg from './textures/Water_2_M_Normal.jpg';
import { BOX_WIDTH } from '../../containers/world/constant';

const epsilon = 1;
const WATER_TEXTURE_SIZE = 128;
const WATER_MSAA_SAMPLES = 0;
const WATER_RTT_UPDATE_INTERVAL = 1 / 80;

function makeWater(geometry, flowMap, normalMaps) {
  return new Water(geometry, {
    color: 0x619bf2,
    opacity: 0.7,
    scale: 0.85,
    textureWidth: WATER_TEXTURE_SIZE,
    textureHeight: WATER_TEXTURE_SIZE,
    multisample: WATER_MSAA_SAMPLES,
    rttUpdateInterval: WATER_RTT_UPDATE_INTERVAL,
    flowSpeed: 0.02,
    reflectivity: 0.32,
    flowMap,
    maskCutoff: 0.42,
    normalMap0: normalMaps.normal0,
    normalMap1: normalMaps.normal1,
    side: BackSide,
  });
}
export default function RiverFlowLayer({ visible = true }) {
  const geometry = useMemo(() => new PlaneGeometry(BOX_WIDTH, BOX_WIDTH), []);
  const loader = useMemo(() => new TextureLoader(), []);
  const flowMaps = useMemo(() => {
    const createFlow = (url) => {
      const map = loader.load(url);
      map.wrapS = map.wrapT = RepeatWrapping;
      map.repeat.set(-1, 1);
      map.offset.x = 1;
      map.colorSpace = NoColorSpace;
      map.generateMipmaps = false;
      map.minFilter = LinearFilter;
      map.magFilter = LinearFilter;
      map.anisotropy = window.maxAnisotropy || 1;
      map.needsUpdate = true;

      return map;
    };
    return {
      pz: createFlow(pzPng),
      px: createFlow(pxPng),
      nx: createFlow(nxPng),
      ny: createFlow(nyPng),
    };
  }, [loader]);

  const normalMaps = useMemo(() => {
    const normal0 = loader.load(normal0Jpg);
    const normal1 = loader.load(normal1Jpg);
    return { normal0, normal1 };
  }, [loader]);

  const waterPz = useMemo(
    () => makeWater(geometry, flowMaps.pz, normalMaps),
    [geometry, flowMaps.pz, normalMaps]
  );
  if (waterPz?.material) {
    waterPz.material.side = DoubleSide;
    waterPz.material.depthWrite = false;
    waterPz.material.depthTest = false;
  }
  waterPz.renderOrder = 100;
  const waterNx = useMemo(
    () => makeWater(geometry, flowMaps.nx, normalMaps),
    [geometry, flowMaps.nx, normalMaps]
  );
  if (waterNx?.material) {
    waterNx.material.side = DoubleSide;
    waterNx.material.depthWrite = false;
    waterNx.material.depthTest = false;
  }
  waterNx.renderOrder = 100;
  const waterPx = useMemo(
    () => makeWater(geometry, flowMaps.px, normalMaps),
    [geometry, flowMaps.px, normalMaps]
  );
  if (waterPx?.material) {
    waterPx.material.side = DoubleSide;
    waterPx.material.depthWrite = false;
    waterPx.material.depthTest = false;
  }
  waterPx.renderOrder = 101;
  const waterNy = useMemo(
    () => makeWater(geometry, flowMaps.ny, normalMaps),
    [geometry, flowMaps.ny, normalMaps]
  );
  if (waterNy?.material) {
    waterNy.material.side = DoubleSide;
    waterNy.material.depthWrite = false;
    waterNy.material.depthTest = false;
  }
  waterNy.renderOrder = 100;
  return (
    <group scale={[-1, 1, 1]} visible={visible}>
      <primitive
        object={waterPz}
        position={[0, 0, BOX_WIDTH / 2 - epsilon]}
        rotation={[0, Math.PI, 0]}
      />
      <primitive
        object={waterNx}
        position={[BOX_WIDTH / 2 - epsilon, 0, 0]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <primitive
        object={waterNy}
        position={[0, -BOX_WIDTH / 2 + epsilon + 0.1, 0]}
        rotation={[-Math.PI / 2, 0, Math.PI]}
      />
      <primitive
        object={waterPx}
        position={[-BOX_WIDTH / 2 + epsilon, 0, 0]}
        rotation={[0, Math.PI / 2, 0]}
      />
    </group>
  );
}
